import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import * as moment from "moment";
import PropTypes from "prop-types";
import DatePicker from "react-multi-date-picker";
import { TextInput, Dropdown } from "hesus-ui-kit";
import { getWinningProbabilityOptions } from "@utils/utils";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";

import "@utils/extra-style.scss";
import "../../AddEditQuote.scss";

import {
  QuoteInfoAndConditionsInfo,
  Title,
  FlexRowHeader,
  GridRow,
} from "../../AddEditQuote.styled";

const QuoteInfoAndCondition = (props) => {
  const {
    t,
    formData,
    setFormData,
    countryId,
    statusDraft,
    statusQuote,
    setCalculateMargin,
    userCurrency,
  } = props;

  const [vatRateList, setVatRateList] = useState([]);
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [paymentMethodList, setPaymentMethodList] = useState([]);
  const [paymentConditionList, setPaymentConditionList] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    if (userCurrency) {
      const currenciesList = Object.entries(userCurrency).map((entry) => {
        return { label: entry[1], value: entry[0] };
      });
      setCurrencies(currenciesList);
    }
  }, [userCurrency]);

  const fetchGeneralVatRate = () => {
    axios
      .get(
        `/general/vatRates?countries=[${countryId}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const vatData = response.data.vatRate.map((vats) => {
          return {
            label: vats.label,
            value: vats.id,
            calculationRate: vats.calculationRate,
          };
        });
        setVatRateList(vatData);
      })
      .catch((error) => {});
  };

  const fetchPaymentTerms = () => {
    axios
      .get(
        `/general/paymentTerms?countries=[${countryId}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const termData = response.data.paymentTerm.map((terms) => {
          return {
            label: terms.label,
            value: terms.id,
          };
        });
        setPaymentTermsList(termData);
      })
      .catch((error) => {});
  };

  const fetchPaymentMethods = () => {
    axios
      .get(
        `/general/paymentMethods?countries=[${countryId}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const termData = response.data.paymentMethod.map((terms) => {
          return {
            label: terms.label,
            value: terms.id,
          };
        });
        setPaymentMethodList(termData);
      })
      .catch((error) => {});
  };

  const fetchPaymentCondition = () => {
    axios
      .get(
        `/general/paymentConditions?countries=[${countryId}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const termData = response.data.paymentCondition.map((terms) => {
          return {
            label: terms.label,
            value: terms.id,
          };
        });
        setPaymentConditionList(termData);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    fetchGeneralVatRate();
    fetchPaymentTerms();
    fetchPaymentMethods();
    fetchPaymentCondition();
  }, []);

  const onNameChange = (e) => {
    setFormData({
      ...formData,
      name: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onTaxRateChange = (e) => {
    const vatInfo = vatRateList?.find(
      (vat) => parseInt(vat.value, 10) === parseInt(e.value, 10)
    );
    setFormData({
      ...formData,
      taxRate: {
        value: vatInfo.calculationRate,
        error: "",
        required: true,
      },
      taxRateId: {
        value: vatInfo.value,
        error: "",
        required: true,
      },
    });
    setCalculateMargin(true);
  };

  const onWinningProbabilityChange = (e) => {
    setFormData({
      ...formData,
      winningProbability: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onExpirationDateChange = (e) => {
    setFormData({
      ...formData,
      expirationDate: {
        value: moment(e.toDate()).format("YYYY-MM-DD"),
        error: "",
        required: true,
      },
    });
  };

  const onEstimatedValidationDateChange = (e) => {
    setFormData({
      ...formData,
      estimatedValidationDate: {
        value: moment(e.toDate()).format("YYYY-MM-DD"),
        error: "",
        required: true,
      },
    });
  };

  const onPaymentMethodChange = (e) => {
    setFormData({
      ...formData,
      paymentMethod: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onPaymentConditionChange = (e) => {
    setFormData({
      ...formData,
      paymentCondition: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onPaymentTermsChange = (e) => {
    setFormData({
      ...formData,
      paymentTerms: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };
  const onCurrencySelect = (e) => {
    setFormData({
      ...formData,
      currency: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  return (
    <>
      <FlexRowHeader>
        <Title>{t("QUOTE_SECTION_CONDITIONS")}</Title>
      </FlexRowHeader>
      <QuoteInfoAndConditionsInfo>
        <GridRow column="1fr">
          <TextInput
            type="text"
            label={t("QUOTE_NAME")}
            onChange={onNameChange}
            name="quote-name"
            id="quote-name"
            value={String(formData.name?.value) || ""}
            isFloat
            required
            errorStyles={{ "font-style": "italic" }}
            labelStyles={
              formData.name.error !== "" ? labelStylesError : labelStyles
            }
            inputStyles={
              formData.name.error !== "" ? inputStylesError : inputStyles
            }
            containerStyles={{ marginTop: "21px" }}
            error={formData.name.error}
            placeholder={t("QUOTE_NAME")}
          />
        </GridRow>
        <GridRow column="1fr 1fr 1fr 1fr">
          <Dropdown
            id="quote-tax-rate"
            label={t("QUOTE_TAX_RATE")}
            name="quote-tax-rate"
            value={formData.taxRateId.value}
            onChange={onTaxRateChange}
            defaultOption={t("QUOTE_TAX_RATE")}
            options={vatRateList}
            required
            disabled={statusDraft}
            labelStyles={
              formData.taxRate.error !== "" ? labelStylesError : labelStyles
            }
            inputStyles={
              formData.taxRate.error !== "" ? inputStylesError : inputStyles
            }
            containerStyles={{ marginBottom: "7px" }}
            error={formData.taxRate.error}
          />
          <Dropdown
            id="quote-winning-probability"
            label={t("QUOTE_WINNING_PROBABILITY")}
            name="quote-winning-probability"
            value={formData.winningProbability.value}
            onChange={onWinningProbabilityChange}
            defaultOption={t("QUOTE_WINNING_PROBABILITY")}
            options={getWinningProbabilityOptions}
            required
            disabled={statusQuote}
            labelStyles={
              formData.winningProbability.error !== ""
                ? labelStylesError
                : labelStyles
            }
            inputStyles={
              formData.winningProbability.error !== ""
                ? inputStylesError
                : inputStyles
            }
            containerStyles={{ marginBottom: "7px" }}
            error={formData.taxRate.error}
          />
          <div
            className={
              formData.expirationDate.error
                ? "add-quote__date-container_start_error"
                : "add-quote__date-container_start quote__date-container_start_required"
            }
          >
            <label htmlFor="quote-expiration-date">
              {t("QUOTE_EXPIRATION_DATE")}
            </label>
            <DatePicker
              inputClass={
                formData.expirationDate.error
                  ? "add-quote__date-input-error"
                  : "add-quote__date-input"
              }
              containerClassName="add-quote__datepicker-container"
              type="text"
              label={t("QUOTE_EXPIRATION_DATE")}
              name="quote-expiration-date"
              id="quote-expiration-date"
              calendarPosition="bottom-center"
              isFloat
              disabled={statusDraft}
              onChange={onExpirationDateChange}
              format={getDatePickerFormat()}
              locale={getCalendarLocale()}
              value={
                getDateFormat(formData.expirationDate.value, "YYYY-MM-DD") || ""
              }
              containerStyles={{ marginTop: "21px" }}
              labelStyles={
                formData.expirationDate.error !== ""
                  ? labelStylesError
                  : labelStyles
              }
              required
              error={formData.expirationDate.error}
              placeholder={t("QUOTE_EXPIRATION_DATE")}
            />
          </div>
          <div
            className={
              formData.estimatedValidationDate.error
                ? "add-quote__date-container_start_error add-quote__date_required"
                : "add-quote__date-container_start quote__date-container_start_required add-quote__date_required"
            }
          >
            <label htmlFor="quote-expiration-date">
              {t("QUOTE_ESTIMATED_VALIDATION_DATE")}
            </label>
            <DatePicker
              inputClass={
                formData.estimatedValidationDate.error
                  ? "add-quote__date-input-error"
                  : "add-quote__date-input"
              }
              containerClassName="add-quote__datepicker-container"
              type="text"
              label={t("QUOTE_ESTIMATED_VALIDATION_DATE")}
              name="quote-expiration-date"
              id="quote-expiration-date"
              calendarPosition="bottom-center"
              isFloat
              required
              disabled={statusQuote}
              onChange={onEstimatedValidationDateChange}
              format={getDatePickerFormat()}
              locale={getCalendarLocale()}
              value={
                getDateFormat(
                  formData.estimatedValidationDate.value,
                  "YYYY-MM-DD"
                ) || ""
              }
              containerStyles={{ marginTop: "21px" }}
              labelStyles={
                formData.estimatedValidationDate.error !== ""
                  ? labelStylesError
                  : labelStyles
              }
              error={formData.estimatedValidationDate.error}
              placeholder={t("QUOTE_ESTIMATED_VALIDATION_DATE")}
            />
          </div>
        </GridRow>
        <GridRow
          column={currencies.length > 1 ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr"}
        >
          {currencies.length > 1 && (
            <Dropdown
              label={t("QUOTE_CURRENCY")}
              name="quote-currency"
              value={String(formData.currency.value)}
              defaultOption={t("SELECT_CURRENCY")}
              options={currencies}
              required
              disabled={statusDraft}
              onChange={onCurrencySelect}
              labelStyles={
                formData.currency.error !== "" ? labelStylesError : labelStyles
              }
              inputStyles={
                formData.currency.error !== "" ? inputStylesError : inputStyles
              }
              containerStyles={{ marginBottom: "7px" }}
              error={formData.currency.error}
            />
          )}
          <Dropdown
            id="quote-payment-method"
            label={t("QUOTE_PAYMENT_METHOD")}
            name="quote-payment-method"
            value={formData.paymentMethod.value}
            onChange={onPaymentMethodChange}
            defaultOption={t("QUOTE_PAYMENT_METHOD")}
            options={paymentMethodList}
            required
            disabled={statusDraft}
            labelStyles={
              formData.paymentMethod.error !== ""
                ? labelStylesError
                : labelStyles
            }
            inputStyles={
              formData.paymentMethod.error !== ""
                ? inputStylesError
                : inputStyles
            }
            containerStyles={{ marginBottom: "7px" }}
            error={formData.paymentMethod.error}
          />
          <Dropdown
            id="quote-payment-condition"
            label={t("QUOTE_PAYMENT_CONDITIONS")}
            name="quote-payment-condition"
            value={formData.paymentCondition.value}
            onChange={onPaymentConditionChange}
            defaultOption={t("QUOTE_PAYMENT_CONDITIONS")}
            options={paymentConditionList}
            required
            disabled={statusDraft}
            labelStyles={
              formData.paymentCondition.error !== ""
                ? labelStylesError
                : labelStyles
            }
            inputStyles={
              formData.paymentCondition.error !== ""
                ? inputStylesError
                : inputStyles
            }
            containerStyles={{ marginBottom: "7px" }}
            error={formData.paymentCondition.error}
          />
          <Dropdown
            id="quote-payment-terms"
            label={t("QUOTE_PAYMENT_TERM")}
            name="quote-payment-terms"
            value={formData.paymentTerms.value}
            onChange={onPaymentTermsChange}
            defaultOption={t("QUOTE_PAYMENT_TERM")}
            options={paymentTermsList}
            required
            disabled={statusDraft}
            labelStyles={
              formData.paymentTerms.error !== ""
                ? labelStylesError
                : labelStyles
            }
            inputStyles={
              formData.paymentTerms.error !== ""
                ? inputStylesError
                : inputStyles
            }
            containerStyles={{ marginBottom: "7px" }}
            error={formData.paymentTerms.error}
          />
        </GridRow>
      </QuoteInfoAndConditionsInfo>
    </>
  );
};

QuoteInfoAndCondition.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  isEditMode: PropTypes.bool,
};
QuoteInfoAndCondition.defaultProps = {
  id: null,
  isEditMode: false,
};

export default withTranslation()(QuoteInfoAndCondition);
